import { useCallback, useEffect, useRef, useState } from "react";
import { getAgentMe, getSipCredentials } from "../api/agent";
import { Softphone, type PhoneState } from "../services/softphone";
import {
  checkMicrophone,
  createLevelMeter,
  sampleMeterFor,
  runEchoCallStage,
  runEchoCallOnLiveSoftphone,
  MIC_METER_DURATION_MS,
  ECHO_CALL_TIMEOUT_MS,
  TEST_CALL_IDLE_TIMEOUT_MS,
  REMEDY_NO_SERVER,
  REMEDY_BUSY,
  REMEDY_STILL_CONNECTING,
  type StageStatus,
  type MediaStageResult,
} from "../services/connectionTest";

interface Stage {
  status: StageStatus;
  remedy?: string;
}

const IDLE_MIC: Stage = { status: "pending" };
const IDLE_MEDIA: Stage = { status: "pending" };
const MIC_FIX_FIRST_REMEDY = "Fix the microphone check above first.";

/** A small horizontal level bar — the "SEE your voice move" proof beads 6vm
 * asks for, not just a pass/fail pill. `level` is 0..1. */
function LevelMeterBar({ level, label }: { level: number; label: string }) {
  const pct = Math.max(0, Math.min(1, level)) * 100;
  return (
    <div className="flex items-center gap-2" role="meter" aria-label={label} aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
      <div className="h-2 flex-1 rounded-full bg-gray-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-emerald-500 transition-[width] duration-100"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function StagePill({ status }: { status: StageStatus }) {
  if (status === "pass") {
    return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800">Pass</span>;
  }
  if (status === "fail") {
    return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800">Fail</span>;
  }
  if (status === "running") {
    return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800">Testing…</span>;
  }
  return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-gray-200 text-gray-600">Not run</span>;
}

export interface ConnectionTestProps {
  extensionId: number;
  /**
   * The console's OWN Softphone instance and its live registration state,
   * threaded down from AgentPanelPage (see AgentConsole's `onPhoneChange`).
   * When the console is already registered (`livePhoneState === "idle"`),
   * the media-path stage reuses THIS instance instead of registering a
   * temporary one — see the component doc comment below for why that
   * matters. Omit (or leave both undefined) to always fall back to a
   * temporary instance, e.g. in a context with no console at all.
   */
  liveSoftphone?: Softphone | null;
  livePhoneState?: PhoneState;
  /**
   * beads F4: bumped every time the console's OWN onState fires, even when
   * the new PhoneState string is identical to the last one (see
   * AgentConsole's `stateSeq` doc comment). `livePhoneState` alone can go a
   * whole real call without ever "changing" from this component's point of
   * view — the stale-busy self-heal path below reuses the console for a new
   * echo call that itself lands back on the exact same "on-call" string a
   * leftover stale value already held, and React's setState bailout on an
   * unchanged primitive would otherwise make the effect below never re-run.
   * Optional so a caller with no console at all (nothing to reuse) simply
   * never triggers a re-run it has no need for.
   */
  livePhoneStateSeq?: number;
  /**
   * Reports whether THIS component currently has its own echo test call up
   * on the reused live softphone — threaded up (via AgentPanelPage) to
   * AgentConsole so it can show an unmistakable "Test call" label instead
   * of "Unknown caller" while that call is on screen. Never true for a real
   * customer call; only ever set by this component's own live-reuse path.
   */
  onTestCallActiveChange?: (active: boolean) => void;
}

/**
 * "Test my connection" — beads beads_php_master_phone_system-6vm.
 *
 * A "Ready" pill in AgentConsole only ever proved SIP registration; nothing
 * checked that the mic works or that audio actually flows. This runs two
 * independent, explicit stages on demand (never automatically — mic prompts
 * belong to a deliberate click, same rule AgentConsole's own goOnline
 * follows):
 *
 *   1. Microphone — getUserMedia + a live input-level meter.
 *   2. Call path — a real SIP call to the dialplan echo extension, proving
 *      the full media path in both directions.
 *
 * Stage 2 MUST NOT register a second session under the agent's own seat
 * while the console is already registered: `pjsip_webrtc.conf`'s
 * `[webrtc-agent]` template sets `max_contacts = 1` / `remove_existing =
 * yes`, and `GET /api/agent/sip-credentials` returns that seat's one static
 * secret every time — a temporary Softphone connecting with the SAME
 * credentials silently evicts the agent's real registration, which then
 * shows as "Ready" in their own console while the PBX has no contact for
 * them at all. So the media-path stage picks its path based on the
 * console's OWN reported state (`livePhoneState`, threaded down from
 * AgentPanelPage) — but that state is a value COPIED up from AgentConsole's
 * React state, one render behind at best, and beads_php_master_phone_system-0uf
 * found it can go stale in both directions live (a "busy" call state that
 * outlives the call that set it; a "Ready" registration that has actually
 * been lost). This component defends against the first direction itself
 * (`reusableLiveSoftphone`, below); see Softphone.verifyRegistration() /
 * AgentConsole's `visibilitychange` handler for the second.
 *
 *   - "idle" (registered, no active call) → REUSE the console's own
 *     instance (`runEchoCallOnLiveSoftphone`, which only ever calls
 *     `.call()`/`.hangup()`, never `.connect()`/`.disconnect()`). This also
 *     exercises the exact registration the agent takes real calls on.
 *   - "ringing" / "on-call" WITHOUT an actual live session behind it
 *     (`liveSoftphone.hasActiveSession()` false — a value left over from a
 *     PREVIOUS call/test that already ended) → treated exactly like "idle"
 *     and reused the same way. A guard that can be permanently wedged by
 *     stale state is worse than no guard.
 *   - "offline" (or no console mounted at all) → nothing live to collide
 *     with; register a temporary instance for the duration of the test
 *     (`runEchoCallStage`), torn down completely afterward.
 *   - "registering" → refuse; there is no session to check yet, and placing
 *     a call before registration finishes would be placing it on an
 *     instance that isn't ready.
 *   - "ringing" / "on-call" WITH a real live session → refuse; this is a
 *     genuine, real call or incoming ring, never interrupted.
 *
 * Reuses the Softphone service directly rather than forking it.
 */
export function ConnectionTest({
  extensionId,
  liveSoftphone,
  livePhoneState,
  livePhoneStateSeq,
  onTestCallActiveChange,
}: ConnectionTestProps) {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [mic, setMic] = useState<Stage>(IDLE_MIC);
  const [media, setMedia] = useState<Stage>(IDLE_MEDIA);
  const [micLevel, setMicLevel] = useState(0);
  const [remoteLevel, setRemoteLevel] = useState(0);
  const [seatName, setSeatName] = useState<string | null>(null);

  const runIdRef = useRef(0);

  // Resolver for the "reuse the live softphone" path's `waitForOnCall()` —
  // Softphone allows only one delegate at a time (already wired to
  // AgentConsole's own callbacks), so this component cannot attach a second
  // listener to the call it just placed on that instance. It observes the
  // SAME `livePhoneState` stream AgentConsole already reports instead (see
  // the effect just below).
  const liveCallWaiterRef = useRef<((established: boolean) => void) | null>(null);
  useEffect(() => {
    if (livePhoneState === "on-call") {
      liveCallWaiterRef.current?.(true);
      liveCallWaiterRef.current = null;
    } else if (livePhoneState === "offline") {
      liveCallWaiterRef.current?.(false);
      liveCallWaiterRef.current = null;
    }
    // beads F4: `livePhoneStateSeq` is in the dependency list on purpose —
    // see its own doc comment on the prop above and AgentConsole's `stateSeq`.
  }, [livePhoneState, livePhoneStateSeq]);

  // --- Item 1/2 of the "keep the test call open" change: a safety-cap
  // timer for the LIVE-reuse path, and the "is the on-call session up
  // right now OUR OWN test call" flag AgentConsole needs for its label. ---
  //
  // Both are armed ONLY from inside runTest() itself, right when THIS
  // component's own call attempt succeeds — never from a generic "state
  // became on-call" watcher, which could just as easily be a real inbound
  // customer call. And both are torn down the instant `livePhoneState`
  // leaves "on-call" for ANY reason (the agent's own Hang up, the far end
  // hanging up, a dropped call) so the timer can never later fire against a
  // LATER, unrelated call placed on this same shared softphone instance.
  const testCallActiveRef = useRef(false);
  const onTestCallActiveChangeRef = useRef(onTestCallActiveChange);
  onTestCallActiveChangeRef.current = onTestCallActiveChange;
  const setTestCallActive = useCallback((active: boolean) => {
    if (testCallActiveRef.current === active) return;
    testCallActiveRef.current = active;
    onTestCallActiveChangeRef.current?.(active);
  }, []);

  const testCallSafetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTestCallSafetyTimeout = useCallback(() => {
    if (testCallSafetyTimeoutRef.current !== null) {
      clearTimeout(testCallSafetyTimeoutRef.current);
      testCallSafetyTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (livePhoneState === "on-call") return;
    clearTestCallSafetyTimeout();
    setTestCallActive(false);
  }, [livePhoneState, clearTestCallSafetyTimeout, setTestCallActive]);

  // Belt-and-suspenders: if this component itself unmounts mid-test (the
  // agent navigates away while the call is up), the timer and the flag it
  // would otherwise clear are cleaned up here too — the `livePhoneState`
  // effect above stops re-running once unmounted.
  // This is unmount-only cleanup — `clearTestCallSafetyTimeout` and
  // `setTestCallActive` are each a stable, no-deps useCallback, so listing
  // them never makes this effect re-run mid-lifetime.
  useEffect(() => {
    return () => {
      clearTestCallSafetyTimeout();
      setTestCallActive(false);
    };
  }, [clearTestCallSafetyTimeout, setTestCallActive]);

  const runTest = useCallback(async () => {
    const myRun = ++runIdRef.current;
    const stillCurrent = () => runIdRef.current === myRun;

    setOpen(true);
    setRunning(true);
    setMic({ status: "running" });
    setMedia({ status: "pending" });
    setMicLevel(0);
    setRemoteLevel(0);

    try {
      // Surface the resolved seat alongside the pass/fail report (beads
      // 6vm's stage 3: "so an agent can tell support exactly what they
      // see"). Best-effort — a failure here must never block the test
      // itself, which is exactly what the mic/call stages are for.
      getAgentMe()
        .then((me) => {
          if (stillCurrent()) setSeatName(me.extension_name);
        })
        .catch(() => {
          if (stillCurrent()) setSeatName(String(extensionId));
        });

      // --- Stage 1: microphone ---------------------------------------
      const micResult = await checkMicrophone();
      if (!micResult.ok) {
        if (stillCurrent()) {
          setMic({ status: "fail", remedy: micResult.remedy });
          setMedia({ status: "fail", remedy: MIC_FIX_FIRST_REMEDY });
        }
        return;
      }

      // From here on `micResult.stream` is a REAL, live capture, and must be
      // released on EVERY exit path — including a run superseded (unmount,
      // or a fresh "Run again") while the OS permission prompt was still
      // open when it finally resolved — or the browser's mic-in-use
      // indicator never clears. A `stillCurrent()` check before this point
      // (checked, then use the stream) would skip the release entirely on
      // that path; try/finally makes the release unconditional instead.
      try {
        if (stillCurrent()) setMic({ status: "pass" });
        const meter = createLevelMeter(micResult.stream);
        await sampleMeterFor(meter, MIC_METER_DURATION_MS, (level) => {
          if (stillCurrent()) setMicLevel(level);
        });
        meter?.stop();
      } finally {
        micResult.stream.getTracks().forEach((t) => t.stop());
      }
      if (!stillCurrent()) return;
      setMicLevel(0);

      // --- Stage 2: media path -----------------------------------------
      setMedia({ status: "running" });

      const onRemoteLevel = (level: number) => {
        if (stillCurrent()) setRemoteLevel(level);
      };

      // "ringing"/"on-call" is only trustworthy as a BUSY signal if there is
      // an actual live session behind it — beads_php_master_phone_system-0uf
      // found it can be a value left over from a previous call/test that
      // already ended, with nothing backing it. A guard that can be
      // permanently wedged by stale state is worse than no guard, so a
      // "claims busy, no session" reading is treated exactly like "idle".
      const staleCallClaim =
        !!liveSoftphone &&
        (livePhoneState === "ringing" || livePhoneState === "on-call") &&
        !liveSoftphone.hasActiveSession();
      const reusableLiveSoftphone =
        liveSoftphone && (livePhoneState === "idle" || staleCallClaim) ? liveSoftphone : null;

      let result: MediaStageResult;
      if (reusableLiveSoftphone) {
        setTestCallActive(true);
        result = await runEchoCallOnLiveSoftphone({
          softphone: reusableLiveSoftphone,
          onRemoteLevel,
          waitForOnCall: () =>
            new Promise<boolean>((resolve) => {
              liveCallWaiterRef.current = resolve;
              setTimeout(() => {
                if (liveCallWaiterRef.current === resolve) {
                  liveCallWaiterRef.current = null;
                  resolve(false);
                }
              }, ECHO_CALL_TIMEOUT_MS);
            }),
        });
        if (result.status === "pass") {
          // Established: leave the call up (see runEchoCallOnLiveSoftphone's
          // doc comment) and arm the forgotten-tab safety cap. Cancelled the
          // instant `livePhoneState` leaves "on-call" (see the effect above)
          // so it can never later fire against a subsequent, unrelated call
          // placed on this same shared softphone.
          testCallSafetyTimeoutRef.current = setTimeout(() => {
            testCallSafetyTimeoutRef.current = null;
            void reusableLiveSoftphone.hangup().catch(() => {
              /* already ended some other way — nothing to clean up */
            });
          }, TEST_CALL_IDLE_TIMEOUT_MS);
        } else {
          setTestCallActive(false);
        }
      } else if (!livePhoneState || livePhoneState === "offline") {
        const audio = new Audio();
        const softphone = new Softphone();
        result = await runEchoCallStage({
          softphone,
          audio,
          getSipCredentials,
          extensionId,
          onRemoteLevel,
        });
      } else if (livePhoneState === "registering") {
        result = { status: "fail", remedy: REMEDY_STILL_CONNECTING, registered: false };
      } else {
        // Genuinely busy: "ringing" or "on-call" WITH a real session behind
        // it — a real incoming ring or an actual in-progress call, never
        // interrupted.
        result = { status: "fail", remedy: REMEDY_BUSY, registered: true };
      }

      if (!stillCurrent()) return;
      setMedia(result.status === "pass" ? { status: "pass" } : { status: "fail", remedy: result.remedy });
      setRemoteLevel(0);
    } finally {
      if (stillCurrent()) setRunning(false);
    }
  }, [extensionId, liveSoftphone, livePhoneState, setTestCallActive]);

  // Abandon a run's remaining state updates if the console unmounts mid-test
  // (e.g. the agent signs out while the self-test is in flight).
  useEffect(() => {
    return () => {
      runIdRef.current += 1;
    };
  }, []);

  const overall: StageStatus =
    mic.status === "fail" || media.status === "fail"
      ? "fail"
      : mic.status === "pass" && media.status === "pass"
        ? "pass"
        : running
          ? "running"
          : "pending";

  const registrationLabel =
    media.status === "pending"
      ? "not tested yet"
      : media.status === "running"
        ? "testing…"
        : media.remedy === REMEDY_NO_SERVER
          ? "failed"
          : media.remedy === REMEDY_BUSY
            ? "skipped — you're on a call"
            : media.remedy === REMEDY_STILL_CONNECTING
              ? "skipped — still connecting"
              : "ok";

  return (
    <div className="bg-white rounded-2xl shadow overflow-hidden mt-4">
      <button
        type="button"
        onClick={() => (open && !running ? setOpen(false) : void runTest())}
        disabled={running}
        className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-gray-50 disabled:cursor-wait"
      >
        <div>
          <div className="text-sm font-semibold text-gray-900">Test my connection</div>
          <div className="text-xs text-gray-500">
            Check your microphone and call path before going available.
          </div>
        </div>
        {open && <StagePill status={overall} />}
      </button>

      {open && (
        <div className="px-6 pb-6 space-y-4 border-t border-gray-100 pt-4">
          {/* --- Stage 1: microphone --- */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-medium text-gray-700">Microphone</span>
              <StagePill status={mic.status} />
            </div>
            {(mic.status === "running" || mic.status === "pass") && (
              <LevelMeterBar level={micLevel} label="Microphone input level" />
            )}
            {mic.remedy && <p className="mt-1 text-xs text-red-600">{mic.remedy}</p>}
          </div>

          {/* --- Stage 2: call path --- */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-medium text-gray-700">Call path (audio)</span>
              <StagePill status={media.status} />
            </div>
            {(media.status === "running" || media.status === "pass") && (
              <LevelMeterBar level={remoteLevel} label="Echoed audio level" />
            )}
            {media.remedy && <p className="mt-1 text-xs text-red-600">{media.remedy}</p>}
          </div>

          <div className="text-xs text-gray-400 pt-2 border-t border-gray-100">
            Seat: Ext {seatName ?? extensionId}
            {" · "}
            Registration: {registrationLabel}
          </div>

          {!running && (mic.status !== "pending" || media.status !== "pending") && (
            <button
              type="button"
              onClick={() => void runTest()}
              className="text-sm font-medium text-brand hover:text-brand-dark"
            >
              Run again
            </button>
          )}
        </div>
      )}
    </div>
  );
}
